import React, { useState, useEffect, useMemo, useRef, useDeferredValue } from 'react';
import { Search, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';

const CategorySelector = ({ value, onChange, viewOnly = false, selectedName = '' }) => {
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [loadedOnce, setLoadedOnce] = useState(false);
  const { t, lang } = useTranslation();
  const dropdownRef = useRef(null);

  // Build parent→children index (O(1) child lookup)
  const childrenMap = useMemo(() => {
    const map = {};
    categories.forEach(c => {
      const pid = c.parent_id ?? '__root__';
      (map[pid] ??= []).push(c);
    });
    return map;
  }, [categories]);

  // Build id→parent_id index (O(1) ancestor tracing)
  const parentMap = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.id] = c.parent_id; });
    return map;
  }, [categories]);

  // Build id→category index (O(1) category lookup)
  const idMap = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.id] = c; });
    return map;
  }, [categories]);

  // Deferred search: keeps UI responsive during typing
  const deferredSearch = useDeferredValue(searchQuery);

  // Memoized search matching (runs on deferred value, not every keystroke)
  const memoizedMatchingIds = useMemo(() => {
    if (!deferredSearch.trim()) return null;
    const q = deferredSearch.toLowerCase();
    const matching = new Set();
    categories.forEach(c => {
      const def = (c.default_name || '').toLowerCase();
      const zh = ((c.labels && c.labels.zh) || '').toLowerCase();
      const en = ((c.labels && c.labels.en) || '').toLowerCase();
      if (def.includes(q) || zh.includes(q) || en.includes(q)) {
        matching.add(c.id);
        let pid = c.parent_id;
        while (pid) {
          matching.add(pid);
          pid = parentMap[pid] ?? null;
        }
      }
    });
    return matching;
  }, [categories, deferredSearch, parentMap]);

  // Lazy-load: fetch only when dropdown opens for the first time
  useEffect(() => {
    if (isOpen && !loadedOnce) {
      setLoading(true);
      fetch('/api/categories')
        .then(res => res.json())
        .then(res => {
          if (res.status === 'success' && res.data) {
            setCategories(res.data);
          } else {
            setCategories([]);
          }
          setLoading(false);
          setLoadedOnce(true);
        })
        .catch(() => {
          setCategories([]);
          setLoading(false);
          setLoadedOnce(true);
        });
    }
  }, [isOpen, loadedOnce]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getLocalizedName = (cat) => {
    if (lang && cat.labels && cat.labels[lang]) return cat.labels[lang];
    return cat.default_name || (cat.labels?.en || '');
  };

  const toggleExpand = (catId) => {
    setExpanded(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  // Build ancestor path (uses idMap for O(1) lookup instead of O(n) find)
  const getPath = (catId) => {
    const result = [];
    let currentId = catId;
    while (currentId) {
      const cat = idMap[currentId];
      if (!cat) break;
      result.unshift(cat);
      currentId = cat.parent_id;
    }
    return result;
  };

  // Render tree or flat search results
  const renderTree = (parentId = null, level = 0) => {
    const isSearching = !!searchQuery;
    const matchingIds = memoizedMatchingIds;

    const children = (childrenMap[parentId ?? '__root__'] || [])
      .filter(c => !isSearching || (matchingIds && matchingIds.has(c.id)));
    if (children.length === 0) return null;

    return children.map(c => {
      const hasKids = (childrenMap[c.id]?.length ?? 0) > 0;
      const isExpanded = expanded[c.id] || false;
      const autoExpand = isSearching && hasKids && (childrenMap[c.id] || []).some(child =>
        matchingIds && matchingIds.has(child.id)
      );

      // Search mode: flat path display
      if (isSearching) {
        const isDirectMatch = matchingIds ? matchingIds.has(c.id) : false;
        const path = getPath(c.id);
        return (
          <div key={c.id}>
            {isDirectMatch && (
              <div
                className={`flex items-center p-2 cursor-pointer text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors ${
                  value === c.id ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 font-medium' : ''
                }`}
                onClick={() => {
                  onChange(c.id);
                  setIsOpen(false);
                  setSearchQuery('');
                }}
              >
                <span className="text-slate-400 dark:text-slate-500 mr-1 shrink-0 w-4">
                  {hasKids ? '▾' : '•'}
                </span>
                <span className="truncate">
                  {path.map((p, i) => (
                    <span key={p.id}>
                      {i > 0 && <span className="text-slate-300 dark:text-slate-600 mx-1">›</span>}
                      <span className={p.id === c.id ? 'font-medium' : 'text-slate-500 dark:text-slate-400'}>
                        {getLocalizedName(p)}
                      </span>
                    </span>
                  ))}
                </span>
              </div>
            )}
            {(autoExpand || isExpanded) && renderTree(c.id, level + 1)}
          </div>
        );
      }

      // Normal tree view
      return (
        <div key={c.id}>
          <div
            className={`flex items-center p-2 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer text-sm transition-colors ${
              value === c.id ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 font-medium' : ''
            }`}
            style={{ paddingLeft: `${level * 14 + 8}px` }}
          >
            <span
              className="shrink-0 w-5 flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
              onClick={(e) => { e.stopPropagation(); hasKids && toggleExpand(c.id); }}
            >
              {hasKids ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="text-xs">•</span>}
            </span>
            <span
              className={`ml-1 truncate ${value === c.id ? 'font-bold' : ''}`}
              onClick={() => {
                onChange(c.id);
                setIsOpen(false);
              }}
            >
              {getLocalizedName(c)}
            </span>
          </div>
          {isExpanded && renderTree(c.id, level + 1)}
        </div>
      );
    });
  };

  const selectedCat = idMap[value] || null;

  // View-only mode: show category name as plain text (no loading, no selector)
  if (viewOnly) {
    const displayName = selectedCat ? getLocalizedName(selectedCat) : (selectedName || t('eav.no_category'));
    return (
      <div className="px-1 py-2">
        <span className="text-sm text-slate-500 dark:text-slate-400 mr-2">{t('eav.category')}:</span>
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {displayName}
        </span>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer flex justify-between items-center bg-white dark:bg-slate-800"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-sm text-slate-600 dark:text-slate-300 truncate">
          {selectedCat ? getLocalizedName(selectedCat) : (selectedName || t('eav.select_category'))}
        </span>
        <ChevronDown size={16} className="text-slate-400 dark:text-slate-500 shrink-0" />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden max-h-80 flex flex-col">
          <div className="p-2 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 shrink-0">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={14} />
              <input
                className="w-full pl-8 pr-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded outline-none focus:ring-1 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
                placeholder="Search across all languages..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto p-1 flex-1">
            {loading ? (
              <div className="flex items-center justify-center gap-2 p-4 text-xs text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('common.loading')}</span>
              </div>
            ) : categories.length > 0 ? (
              renderTree()
            ) : (
              <div className="p-4 text-center text-xs text-slate-400">{t('eav.no_matching_categories')}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategorySelector;
